<!-- NetWorthChart.vue -->
<template>
  <div class="chart-container">
    <div class="chart-header">
      <h3>Net Worth Visualization</h3>
      <div class="chart-controls">
        <button 
          @click="toggleView" 
          class="btn-toggle"
          :class="{ active: showDetailed }"
        >
          {{ showDetailed ? 'Show Summary' : 'Show Details' }}
        </button>
      </div>
    </div>

    <div class="chart-wrapper">
      <HighChartsWrapper
        name="net-worth"
        :chart-type="chartType"
        :chart-options="highchartsOptions"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'NetWorthChart',
  components: {
    HighChartsWrapper
  },
  props: {
    yearRows: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const showDetailed = ref(false);
    const chartType = ref('column');

    // Colors for different categories
    const colors = {
      assets: '#2ecc71',      // Green for assets
      liabilities: '#e74c3c', // Red for liabilities  
      networth: '#3498db',    // Blue for net worth
      // Additional colors for individual items
      assetPalette: [
        '#27ae60', '#2ecc71', '#58d68d', '#82e0aa', '#abebc6',
        '#d5f4e6', '#22c55e', '#16a34a', '#15803d', '#166534'
      ],
      liabilityPalette: [
        '#c0392b', '#e74c3c', '#ec7063', '#f1948a', '#f5b7b1',
        '#fadbd8', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'
      ]
    };

    // Prepare chart data and options
    const highchartsOptions = computed(() => {
      const years = props.yearRows.map(r => r.year);
      
      let series = [];
      
      if (showDetailed.value) {
        // Individual items view - separate asset and liability items
        const assetItems = props.items
          .map((item, index) => ({ ...item, originalIndex: index }))
          .filter(item => item.type === 'asset');
        const liabilityItems = props.items
          .map((item, index) => ({ ...item, originalIndex: index }))
          .filter(item => item.type === 'liability');
        
        // Add individual asset items as stacked columns
        assetItems.forEach((item, index) => {
          const data = props.yearRows.map(row => {
            const value = row[`nw_${item.id}`] || 0;
            return Math.abs(value);
          });
          
          // Create display name for duplicates
          let displayName = item.name || `Asset ${index + 1}`;
          if (item.category) {
            displayName = `${item.name} (${item.category})`;
          }
          
          series.push({
            name: displayName,
            data: data,
            type: 'column',
            color: colors.assetPalette[index % colors.assetPalette.length],
            stack: 'networth'
          });
        });
        
        // Add individual liability items as negative stacked columns
        liabilityItems.forEach((item, index) => {
          const data = props.yearRows.map(row => {
            const value = row[`nw_${item.id}`] || 0;
            // Liabilities should be negative for stacking
            return -Math.abs(value);
          });
          
          let displayName = item.name || `Liability ${index + 1}`;
          
          series.push({
            name: displayName,
            data: data,
            type: 'column',
            color: colors.liabilityPalette[index % colors.liabilityPalette.length],
            stack: 'networth'
          });
        });

        // Add net worth as a line
        series.push({
          name: 'Net Worth',
          data: props.yearRows.map(row => row.netWorth || 0),
          type: 'spline',
          color: colors.networth,
          lineWidth: 3,
          marker: { enabled: true, radius: 5 },
          zIndex: 10
        });

      } else {
        // Summary view - aggregated assets and liabilities
        const assetData = props.yearRows.map(row => row.totalAssets || 0);
        const liabilityData = props.yearRows.map(row => -(row.totalLiabilities || 0)); // Negative for stacking
        const netWorthData = props.yearRows.map(row => row.netWorth || 0);
        
        series = [
          { 
            name: 'Total Assets', 
            data: assetData, 
            type: 'column',
            color: colors.assets,
            stack: 'networth'
          },
          { 
            name: 'Total Liabilities', 
            data: liabilityData, 
            type: 'column',
            color: colors.liabilities,
            stack: 'networth'
          },
          { 
            name: 'Net Worth', 
            data: netWorthData, 
            type: 'spline',
            color: colors.networth,
            lineWidth: 4,
            marker: { enabled: true, radius: 5 },
            zIndex: 10
          }
        ];
      }

      const baseOptions = {
        chart: { 
          type: chartType.value,
          height: 400
        },
        title: { text: null },
        xAxis: { 
          categories: years,
          title: { text: 'Year' }
        },
        yAxis: { 
          title: { text: 'Amount ($)' },
          labels: {
            formatter: function() {
              return props.formatCurrency(this.value);
            }
          },
          plotLines: [{
            value: 0,
            color: '#888',
            width: 1,
            zIndex: 4,
            dashStyle: 'dash'
          }]
        },
        tooltip: { 
          shared: true, 
          formatter: function() {
            let tooltip = `<b>Year ${this.x}</b><br/>`;
            
            // Sort points to show assets first, then liabilities, then net worth
            const sortedPoints = this.points.sort((a, b) => {
              if (a.series.name === 'Net Worth') return 1;
              if (b.series.name === 'Net Worth') return -1;
              if (a.y >= 0 && b.y < 0) return -1;
              if (a.y < 0 && b.y >= 0) return 1;
              return 0;
            });
            
            sortedPoints.forEach(point => {
              const value = Math.abs(point.y);
              const prefix = point.y < 0 && point.series.name !== 'Net Worth' ? '-' : '';
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${prefix}${props.formatCurrency(value)}</b><br/>`;
            });
            return tooltip;
          }
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom'
        },
        plotOptions: {
          column: { 
            stacking: 'normal',
            borderWidth: 0,
            dataLabels: { enabled: false }
          },
          spline: {
            lineWidth: 3,
            marker: {
              enabled: true,
              radius: 4
            }
          },
          series: {
            borderWidth: 0
          }
        },
        series: series
      };

      return baseOptions;
    });

    const toggleView = () => {
      showDetailed.value = !showDetailed.value;
    };

    return {
      showDetailed,
      chartType,
      highchartsOptions,
      toggleView
    };
  }
};
</script>